import React from 'react';
import omit from 'lodash/omit';
import { css } from '@emotion/core';
import { useSelector } from 'react-redux';
import { OverlayTrigger } from 'react-bootstrap';

import t from 'react-translate';
import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import NvIcon from 'shared/components/nv-icon';
import { mergeRefs } from 'shared/react-utils';
import usePrevious from 'shared/hooks/use-previous';
import NvTooltip from 'shared/components/nv-tooltip';
import { getCurrentUser } from 'redux/selectors/users';
import useClickOutside from 'shared/hooks/use-click-outside';
import ClickableContainer from 'components/clickable-container';
import useLanguageTranslation from 'automated_translation/hooks/use-language-translation';
import {
  setTranslationPreference,
  unsetTranslationPreference,
} from 'redux/actions/users';
import {
  normalFontWeight,
  semiBoldFontWeight,
} from 'styles/global_defaults/fonts';
import {
  gray2,
  gray3,
  gray5,
  gray6,
  white,
  primary,
  yellow,
  gray4,
} from 'styles/global_defaults/colors';
import {
  halfSpacing,
  popoverZIndex,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import LanguagePicker, {
  LanguageValue,
  AUTOMATED_TRANSLATION_LANGUAGES,
} from 'automated_translation/components/language-picker';
import { config } from '../../../config/pendo.config.json';

const AutomatedTranslationTranslator = () => {
  const dispatch = useAppDispatch();
  const currentUser = useSelector(getCurrentUser);
  const buttonRef = React.useRef<HTMLDivElement>();
  const getLanguageTranslation = useLanguageTranslation();
  const [showOverlay, setShowOverlay] = React.useState(false);
  const { injectServices } = React.useContext(AngularContext);
  const [CurrentUserManager] = injectServices(['CurrentUserManager']);

  const lastTranslationValue = currentUser.lastTranslationPreferenceLanguage && {
    isoCode: currentUser.lastTranslationPreferenceLanguage,
    label: getLanguageTranslation(AUTOMATED_TRANSLATION_LANGUAGES.find((language) => language.isoCode === currentUser.lastTranslationPreferenceLanguage).translationKey),
  };

  const platformLanguageValue = (() => {
    const platformLanguageOption = AUTOMATED_TRANSLATION_LANGUAGES.find((language) => language.translationKey === currentUser.platformLanguage.toUpperCase());

    if (platformLanguageOption) {
      return {
        isoCode: platformLanguageOption.isoCode,
        label: getLanguageTranslation(platformLanguageOption.translationKey),
      };
    }

    return null;
  })();

  const initialLanguage: LanguageValue = lastTranslationValue || platformLanguageValue;

  const [language, setLanguage] = React.useState(initialLanguage);
  const { isTranslating } = useSelector((state) => state.app.lecturePage);
  const { currentCatalogId, lecturePage } = useSelector((state) => state.app);

  const hasTranslationSet = !!currentUser.translationPreferenceLanguage;

  const notTranslatingIconColor = hasTranslationSet ? yellow : white;

  const iconColor = isTranslating ? gray4 : notTranslatingIconColor;

  const previousPreferenceLanguage = usePrevious(currentUser.translationPreferenceLanguage);

  const styles = css`
    .icon {
      color: ${iconColor};
    }
  `;

  const applyTranslation = () => {
    setShowOverlay(false);

    dispatch(setTranslationPreference({
      language: language.isoCode,
      catalogId: currentCatalogId,
      lecturePageId: lecturePage.currentLectureId,
    })).then(() => {
      CurrentUserManager.user.updateFromReact({
        translationPreferenceLanguage: language.isoCode,
        lastTranslationPreferenceLanguage: language.isoCode,
      });
    });
  };

  if (isTranslating || hasTranslationSet) {
    const unsetTranslation = () => dispatch(unsetTranslationPreference());

    const shouldShowTranslatingTooltip = isTranslating && !previousPreferenceLanguage;

    return (
      <NvTooltip
        show={shouldShowTranslatingTooltip ? true : undefined}
        text={shouldShowTranslatingTooltip ? t.AUTOMATED_TRANSLATION.TRANSLATING() : t.AUTOMATED_TRANSLATION.TURN_OFF()}
      >
        <ClickableContainer
          css={styles}
          layoutOnly={isTranslating}
          onClick={unsetTranslation}
          {...(hasTranslationSet ? {
            'data-qa': config.pendo.automatedTranslation.stopTranslating,
          } : {})}
        >
          <NvIcon
            size='medium'
            icon='translate'
          />
        </ClickableContainer>
      </NvTooltip>
    );
  }

  return (
    <OverlayTrigger
      show={showOverlay}
      placement='top-end'
      transition={false}
      overlay={(props) => (
        <AccessibleOverlay
          onClose={(closedWithKeyboard) => {
            setShowOverlay(false);

            if (closedWithKeyboard) {
              buttonRef.current.focus();
            }
          }}
          {...omit(props, ['popper', 'arrowProps']) as any}
        >
          <div className='mb-2 label'>{t.AUTOMATED_TRANSLATION.SELECT_LABEL()}</div>
          <LanguagePicker
            value={language}
            className='picker'
            onChange={setLanguage}
            dataQa={config.pendo.automatedTranslation.languagesPicker}
          />
          <div className='text-center'>
            <ClickableContainer
              disabled={!language}
              onClick={applyTranslation}
              className='text-small translate-button'
              data-qa={config.pendo.automatedTranslation.applyTranslation}
            >
              {t.AUTOMATED_TRANSLATION.AUTO_TRANSLATE()}
            </ClickableContainer>
          </div>
        </AccessibleOverlay>
      )}
    >
      {({ ref, ...props }) => (
        <NvTooltip text={t.AUTOMATED_TRANSLATION.AUTO_TRANSLATE()}>
          <ClickableContainer
            css={styles}
            ref={mergeRefs(ref, buttonRef)}
            onClick={() => setShowOverlay(true)}
            data-qa={config.pendo.automatedTranslation.automatedTranslationLecturePageOption}
            {...props}
          >
            <NvIcon
              size='medium'
              icon='translate'
            />
          </ClickableContainer>
        </NvTooltip>
      )}
    </OverlayTrigger>
  );
};

type AccessibleOverlayProps = Omit<React.ComponentProps<'div'>, 'tabIndex' | 'className'> & {
  show: boolean,
  onClose: (closedWithKeyboard: boolean) => void,
};

const AccessibleOverlay = React.forwardRef<HTMLDivElement, AccessibleOverlayProps>((props, ref) => {
  const {
    show,
    onClose,
    ...restProps
  } = props;

  const innerRef = React.useRef<HTMLDivElement>();
  const onCloseRef = React.useRef<Function>();
  onCloseRef.current = onClose;

  const styles = css`
    width: 340px;
    border-radius: 4px;
    background-color: white;
    border: 1px solid ${gray5};
    z-index: ${popoverZIndex};
    padding: ${standardSpacing}px;

    .label {
      color: ${gray2};
    }

    .picker {
      margin-bottom: ${standardSpacing}px;
    }

    .translate-button {
      color: ${white};
      border-radius: 4px;
      display: inline-block;
      background-color: ${primary};
      font-weight: ${semiBoldFontWeight};
      padding: ${quarterSpacing}px ${halfSpacing}px;

      &[aria-disabled='true'] {
        opacity: .65;
        color: ${gray3};
        background-color: ${gray6};
        font-weight: ${normalFontWeight};
      }
    }
  `;


  React.useEffect(() => {
    if (innerRef.current) {
      innerRef.current.focus();
    }
  }, [show]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose(true);
    }
  };

  useClickOutside(innerRef, () => onClose(false));

  return (
    <div
      css={styles}
      tabIndex={-1}
      className='react-app'
      onKeyDown={handleKeyDown}
      ref={mergeRefs(ref, innerRef)}
      {...restProps}
    />
  );
});

export default AutomatedTranslationTranslator;
